import { z } from 'zod';
import { LocalTimeOfDay } from './messaging.js';
import { paymentsConfigOf, paymentsUsable } from './payments.js';
import { bufferDefaultsOf, leadTimeOf } from './scheduling-policy.js';
import { guestBookingOf, pickupExpiryDaysOf, requestHoldHoursOf, shopFulfilmentOf, waiverOf, } from './tenant.js';
/**
 * **The policies the page states are the policies the engine keeps**
 * (`docs/guest-site-plan.md` GS2; the owner's ruling 2 of 2026-09-21).
 *
 * ## The problem this module closes
 *
 * A tenant's booking site has a `policies` block, and until this wave every row
 * in it was prose somebody typed — *"Cancellation / 24 hours' notice"* — that
 * `GET /v1/profile` republished to the AI agent as if it were a rule. Nothing
 * the engine enforces says 24 hours. Love Spa's printed card says 24 hours, the
 * owner says *"the night before"*, and the platform, asked, would have quoted
 * the card to a guest and to the agent in the same breath. That is the one gap
 * on the plan that can make both products lie at once.
 *
 * ## The rule, in one sentence
 *
 * **The engine's rule wins over an authored row, and the row is flagged.** A
 * rule the engine actually applies — the lead time `POST /api/bookings` refuses
 * under, the hours a request holds its slot, the days a ready order waits on
 * the shelf, the deposit a payment link asks for, the house gap between
 * appointments, the waiver a sailing asks a guest to tick — is *derived* here
 * from `tenant_config`, as numbers, never as prose. Each surface then words
 * the number in its own language from its own catalog. An authored row that
 * claims to state one of those (`kind` names it) is **not published**; the
 * derived row is, and the console badges the authored one so the owner sees
 * why. An authored row that states something the engine has no opinion on —
 * *what to bring*, *parking*, and **cancellation** — publishes as the tenant's
 * own words, labelled as such.
 *
 * ## Why there is no `cancellation` row, and must not be
 *
 * The engine has **no cancellation policy today**. `apps/guest-api`'s cancel
 * route charges nothing and says why: "the policy — notice windows, deposits, a
 * charge — is MVP 4's, arriving with the money surfaces. A zero here is not a
 * decision that cancellation is free; it is the absence of the module that
 * would price it." Deriving a window from that absence would be the platform
 * stating a rule nobody set, which is the exact failure this module exists to
 * stop. So `cancellation` is an *authored* kind only: the tenant's own words,
 * published as such, until there is a module whose number could be quoted.
 *
 * ## One derivation, no second implementation
 *
 * Every number below is read through the reader the engine itself uses —
 * `leadTimeOf`, `requestHoldHoursOf`, `pickupExpiryDaysOf`, `bufferDefaultsOf`
 * (the policy half of `buffersFor`), `paymentsConfigOf`, `waiverOf` — so this
 * file cannot disagree with the enforcement without one of those changing, and
 * a rule not in effect for a tenant derives nothing at all: a spa with no shop
 * has no `pickup_expiry` row, a tenant with no deposit policy has no `deposit`
 * row. Capabilities are a projection, never configured (spec §3.3); so is this.
 */
/* ------------------------------------------------------------------ */
/* The vocabulary                                                      */
/* ------------------------------------------------------------------ */
/** The rules the engine keeps and this module can therefore state. */
export const DerivedPolicyKind = z.enum([
    'lead_time',
    'request_hold',
    'pickup_expiry',
    'deposit',
    'house_gap',
    'waiver',
]);
/**
 * What an authored row may say it is about. `cancellation` is here and not in
 * `DerivedPolicyKind`, for the reason in the header: the engine does not price
 * one, so the tenant's words are the only words there are. `other` is the
 * explicit "this is not a rule the engine keeps" — *what to bring*, *parking* —
 * and the absence of a kind means the same, less deliberately.
 */
export const AuthoredPolicyKind = z.enum([
    'cancellation',
    'lead_time',
    'request_hold',
    'pickup_expiry',
    'deposit',
    'other',
]);
/**
 * A derived row: **structured, numbers, never prose.** Each surface words it —
 * the guest site from `apps/site`'s catalog, the console from its own, the
 * partner from whatever its agent reads aloud — because a sentence minted here
 * would be one language on three surfaces that each already have two.
 */
export const DerivedPolicy = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('lead_time'),
        /** The floor, in minutes. May be `0` when only the cut-off is set. */
        minutes: z.number().int().min(0),
        /** Local `HH:MM` after which today is no longer offered at all. */
        same_day_cutoff: LocalTimeOfDay.optional(),
    }),
    z.object({ kind: z.literal('request_hold'), hours: z.number().int().min(1) }),
    z.object({ kind: z.literal('pickup_expiry'), days: z.number().int().min(1) }),
    z.object({
        kind: z.literal('deposit'),
        mode: z.enum(['percent', 'fixed']),
        /** Whole percent, present when `mode = percent`. */
        percent: z.number().int().min(1).max(100).optional(),
        /** Present when `mode = fixed`. In the tenant's currency. */
        fixed_cents: z.number().int().min(1).optional(),
        /** The floor a percentage never goes under. */
        min_cents: z.number().int().min(1),
    }),
    z.object({
        kind: z.literal('house_gap'),
        before_min: z.number().int().min(0),
        after_min: z.number().int().min(0),
    }),
    z.object({
        kind: z.literal('waiver'),
        title: z.string().min(1).max(200),
        version: z.number().int().min(1),
    }),
]);
/** An authored row as the branding document stores it, plus its optional kind. */
export const AuthoredPolicy = z.object({
    label: z.string(),
    value: z.string(),
    kind: AuthoredPolicyKind.optional(),
});
/* ------------------------------------------------------------------ */
/* The derivation                                                      */
/* ------------------------------------------------------------------ */
/**
 * The rules in effect for this tenant, in a fixed order.
 *
 * The order is the order a guest meets them — how far ahead to book, what
 * happens to a request, what it costs to hold, how the day is spaced, how long
 * the shelf waits, what is signed — and it is fixed so that the same config
 * always derives the same list byte for byte, which the guest wire's
 * "byte-identical for a plain tenant" tests rely on.
 *
 * Every arm is *only when in effect*:
 *
 *  * `lead_time` — `leadTimeOf` answers `0`/`null` for the whole fleet before
 *    the setting existed; that is "no floor beyond the one every surface has
 *    always had" and is not a policy worth a sentence.
 *  * `request_hold` — only a site that takes requests (`guest_booking` is
 *    `request` or `trusted`) ever consults `request_hold_hours`; for an `open`
 *    site the number exists and is never asked for, so stating it would be
 *    stating a rule that never runs.
 *  * `pickup_expiry` — only when the shop offers pickup (`shop_fulfilment`),
 *    because the expiry is read once, when a reservation becomes ready, and a
 *    shop that ships has no shelf for it to be about.
 *  * `deposit` — only when the policy says `required` **and** the rail is
 *    usable (`paymentsUsable`): a required deposit on a tenant that cannot mint
 *    a link is a policy nothing enforces.
 *  * `house_gap` — only when a default is set; `buffersFor` returns the
 *    service's own pair by identity otherwise.
 *  * `waiver` — only when one is published (`waiverOf` is `null` otherwise).
 *    The title and version travel; the text does not — a rule row says *that* a
 *    waiver is asked for, and the waiver itself is served where it is signed.
 */
export function derivedPolicies(config) {
    const out = [];
    const lead = leadTimeOf(config);
    if (lead.min_lead_minutes > 0 || lead.same_day_cutoff !== null) {
        out.push({
            kind: 'lead_time',
            minutes: lead.min_lead_minutes,
            ...(lead.same_day_cutoff === null ? {} : { same_day_cutoff: lead.same_day_cutoff }),
        });
    }
    if (guestBookingOf(config) !== 'open') {
        out.push({ kind: 'request_hold', hours: requestHoldHoursOf(config) });
    }
    const payments = paymentsConfigOf(config ?? {});
    if (payments !== null && payments.deposit.required && paymentsUsable(payments)) {
        const { deposit } = payments;
        out.push({
            kind: 'deposit',
            mode: deposit.mode,
            ...(deposit.mode === 'percent' ? { percent: deposit.percent } : {}),
            ...(deposit.mode === 'fixed' ? { fixed_cents: deposit.fixed_cents } : {}),
            min_cents: deposit.min_cents,
        });
    }
    const buffers = bufferDefaultsOf(config);
    if (buffers.before_min > 0 || buffers.after_min > 0) {
        out.push({ kind: 'house_gap', before_min: buffers.before_min, after_min: buffers.after_min });
    }
    if (shopFulfilmentOf(config).pickup) {
        out.push({ kind: 'pickup_expiry', days: pickupExpiryDaysOf(config) });
    }
    const waiver = waiverOf(config);
    if (waiver !== null) {
        out.push({ kind: 'waiver', title: waiver.title, version: waiver.version });
    }
    return out;
}
/**
 * Apply the owner's ruling to one tenant's rows.
 *
 * An authored row is overridden when its `kind` names a rule the engine
 * derives **for this tenant** — a row tagged `deposit` on a tenant with no
 * deposit policy is not overridden by anything, and publishes: there is no
 * engine row to disagree with, and the tenant's words are the only words. A
 * row with no kind, `other`, or `cancellation` always publishes.
 *
 * Suspicion is a heuristic over the words of an *untagged* row (a tagged row
 * has already been classified by a person, and second-guessing that is not the
 * software's place). It is computed here rather than in the console so that
 * the console and any future report say the same thing about the same row.
 */
export function resolvePolicies(config, rows) {
    const derived = derivedPolicies(config);
    const byKind = new Map(derived.map((d) => [d.kind, d]));
    const authored = [];
    const overridden = [];
    const suspected = [];
    rows.forEach((row, index) => {
        const engine = row.kind === undefined ? undefined : overridingKind(row.kind, byKind);
        if (engine !== undefined) {
            overridden.push({ index, row, by: engine });
            return;
        }
        authored.push(row);
        if (row.kind === undefined) {
            const kinds = suspectedPolicyKinds(row, derived);
            if (kinds.length > 0)
                suspected.push({ index, row, kinds });
        }
    });
    return { derived, authored, overridden, suspected };
}
function overridingKind(kind, byKind) {
    // `cancellation` and `other` are never in the derived vocabulary, so the
    // lookup answers `undefined` for them without a special case — which is the
    // property this function exists to keep: the list of what the engine derives
    // is the list of what can override, and nothing else.
    const parsed = DerivedPolicyKind.safeParse(kind);
    return parsed.success ? byKind.get(parsed.data) : undefined;
}
/* ------------------------------------------------------------------ */
/* The hint                                                            */
/* ------------------------------------------------------------------ */
/**
 * Words that tend to appear in a row about each rule, in the two languages
 * the fleet writes in. **Deliberately loose and deliberately not a gate.** A
 * regex over prose cannot know that *"24 hours' notice"* is about cancelling
 * and not about booking ahead — so any row that mentions cancelling is left
 * alone first, and the rest is matched only against kinds actually in effect
 * for the tenant, because "this looks like a deposit rule" is only worth
 * saying when there is a deposit rule to check it against.
 */
const CANCELLATION_WORDS = /\b(cancel\w*|no[- ]show\w*|refund\w*|reembols\w*|anulaci\w*)/i;
const VOCABULARY = {
    lead_time: /\b(lead|advance|ahead|notice|antelaci[oó]n|anticipaci[oó]n|antemano|aviso)\b/i,
    request_hold: /\b(hold\w*|held|pending|request\w*|solicitud\w*|pendiente\w*|espera)\b/i,
    pickup_expiry: /\b(pick[- ]?up\w*|collect\w*|shelf|recog\w*|retir\w*)\b/i,
    deposit: /\b(deposit\w*|dep[oó]sito\w*|anticipo\w*|se[ñn]al|prepa\w*)\b/i,
    house_gap: /\b(buffer\w*|gap|between (?:appointments|clients|guests)|entre citas|intervalo\w*)\b/i,
    waiver: /\b(waiver\w*|release form|exenci[oó]n|renuncia\w*|consentimiento\w*)\b/i,
};
/**
 * Which of the rules in effect this untagged row's words resemble. Empty for a
 * row about cancelling — the engine has nothing to say about that — and empty
 * for a tenant with no rules at all, since there is nothing to check against.
 */
export function suspectedPolicyKinds(row, derived) {
    const text = `${row.label} ${row.value}`;
    if (CANCELLATION_WORDS.test(text))
        return [];
    return derived.map((d) => d.kind).filter((kind) => VOCABULARY[kind].test(text));
}
//# sourceMappingURL=policies.js.map